/*
 * url-discount-price.js
 * ---------------------------------------------------------------------------
 * Product page: show a discount-code strikethrough price driven by URL params,
 * reusing the theme's native sale style (<del>/<ins>) and a cart-style discount tag.
 *
 * DISPLAY ONLY. The real discount is applied by Shopify at checkout. Use a native
 * Shopify share-discount link so the code is guaranteed to apply, and carry the
 * display value in the redirect target, e.g.:
 *
 *     /discount/SUMMER20?redirect=/products/the-shoe?dp=20
 *
 * Shopify applies SUMMER20 to the cart/checkout and redirects to the PDP with
 * ?dp=20, which this script renders as <del>original</del> <ins>discounted</ins>
 * plus a cart-style "CODE (-$X)" tag.
 *
 * Supported URL params:
 *     dp        percent off            ?dp=20        -> 20% off
 *     da        fixed amount off       ?da=10        -> 10 (displayed currency) off
 *     discount  Shopify code (native)  ?discount=X   -> shown on the tag; can also drive
 *                                                       display via the CODE_DISCOUNTS map
 *
 * The money format (symbol, separators, decimals) is taken verbatim from the price
 * already on the page, so "US$119.00" stays "US$..." — only the amount changes.
 * The front-end value MUST match the discount code configured in the Shopify admin,
 * otherwise the price shown here will not match the checkout total.
 *
 * Once seen in the URL, the discount is persisted (localStorage, see PERSIST_TTL_MS)
 * and BOUND to that product's handle, so it survives a refresh / returning to that
 * product when the URL param is gone, but does NOT bleed onto other products.
 *
 * A/B TEST: eligible visitors (arrived via a discount link) are split into
 * control (original price style) vs test (discount strikethrough + tag). The
 * variant is set as an OpenPanel global property (AB_VARIANT_PROP) so every
 * conversion event is segmented by it, plus a one-off "PDP Discount Price Viewed"
 * exposure event. See the AB_* config below.
 */
(function () {
  'use strict';

  // Optional: map a discount code to a display discount so a bare ?discount=CODE
  // also renders a strikethrough. Leave empty to rely on the dp / da params.
  //   Example: { SUMMER20: { type: 'percent', value: 20 }, FLAT10: { type: 'amount', value: 10 } }
  var CODE_DISCOUNTS = {};

  // Persist the discount so it survives a refresh / navigation after the URL param
  // is gone (mirrors how Shopify keeps the discount code in the cart session). The
  // real discount is still applied & validated by Shopify at checkout.
  var STORAGE_KEY = 'wade_url_discount';
  var PERSIST_TTL_MS = 7 * 24 * 60 * 60 * 1000; // how long to remember it; 0 = forever

  // --- A/B test -------------------------------------------------------------
  // Compares the ORIGINAL price style (control) vs the discount strikethrough +
  // tag (test) for visitors who arrived via a discount link. Both groups still get
  // the real discount at checkout (Shopify); the test only changes what the PDP
  // shows. Tracking goes through OpenPanel (window.op), the same sink hero-ab-test
  // uses: the variant is set as a global property so every conversion event
  // ("Product Added to Cart", checkout, ...) is segmented by it, plus a one-off
  // exposure event. Bucketing is stable per visitor (hash of key + visitor id).
  var AB_EXPERIMENT_KEY = 'pdp_discount_price_2026';
  var AB_ALLOCATION = 100;                              // % of eligible visitors shown the test
  var AB_OVERRIDE_PARAM = 'pdp_discount_variant';      // ?pdp_discount_variant=test|control (QA)
  var AB_VARIANT_PROP = 'pdp_discount_price_variant';  // OpenPanel global property name
  var AB_VISITOR_KEY = 'wade-ab:visitor-id';
  var AB_ASSIGN_KEY = 'wade-ab:variant:' + AB_EXPERIMENT_KEY;
  var AB_EXPOSED_KEY = 'wade-ab:exposed:' + AB_EXPERIMENT_KEY;

  var PRICE_SELECTOR = '.t4s-product-price[data-product-price]';
  // Same icon the cart uses (#icon-cart-tag), inlined so it works without the cart in the DOM.
  var TAG_ICON = '<svg viewBox="0 0 448 512" width="20" aria-hidden="true" focusable="false">' +
    '<path d="M48 32H197.5C214.5 32 230.7 38.74 242.7 50.75L418.7 226.7C443.7 251.7 443.7 292.3 418.7 317.3L285.3 450.7C260.3 475.7 219.7 475.7 194.7 450.7L18.75 274.7C6.743 262.7 0 246.5 0 229.5V80C0 53.49 21.49 32 48 32L48 32zM112 176C129.7 176 144 161.7 144 144C144 126.3 129.7 112 112 112C94.33 112 80 126.3 80 144C80 161.7 94.33 176 112 176z"/></svg>';

  function getParams() {
    try { return new URLSearchParams(window.location.search); }
    catch (e) { return null; }
  }

  function parseNumber(v) {
    if (v == null) return NaN;
    var n = parseFloat(String(v).replace(/[^0-9.\-]/g, ''));
    return isFinite(n) ? n : NaN;
  }

  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  // Discount codes are alphanumeric; strip anything else so a crafted ?discount=
  // value can never carry HTML into the DOM (defense against persisted XSS).
  function sanitizeCode(raw) {
    return String(raw || '').trim().toUpperCase().replace(/[^A-Z0-9_-]/g, '').slice(0, 64);
  }

  // Returns { type:'percent'|'amount', value:Number, code:String } or null
  function resolveDiscount(p) {
    if (!p) return null;
    var code = sanitizeCode(p.get('discount'));
    var dp = parseNumber(p.get('dp'));
    if (isFinite(dp) && dp > 0) return { type: 'percent', value: Math.min(dp, 100), code: code };
    var da = parseNumber(p.get('da'));
    if (isFinite(da) && da > 0) return { type: 'amount', value: da, code: code };
    if (code && CODE_DISCOUNTS[code]) {
      var c = CODE_DISCOUNTS[code];
      if (c && c.value > 0) return { type: c.type === 'amount' ? 'amount' : 'percent', value: c.value, code: code };
    }
    return null;
  }

  function discountedCents(baseCents, discount) {
    if (!discount) return baseCents;
    if (discount.type === 'percent') return Math.round(baseCents * (100 - discount.value) / 100);
    return Math.max(0, baseCents - Math.round(discount.value * 100));
  }

  function saveDiscount(d, code, handle) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        type: d.type, value: d.value, code: code || '', handle: handle || '', ts: Date.now()
      }));
    } catch (e) {}
  }

  function loadDiscount() {
    try {
      var o = JSON.parse(localStorage.getItem(STORAGE_KEY));
      if (!o || !(o.value > 0)) return null;
      if (PERSIST_TTL_MS > 0 && o.ts && (Date.now() - o.ts) > PERSIST_TTL_MS) {
        localStorage.removeItem(STORAGE_KEY);
        return null;
      }
      return {
        discount: { type: o.type === 'amount' ? 'amount' : 'percent', value: o.value },
        code: o.code || '',
        handle: o.handle || ''
      };
    } catch (e) { return null; }
  }

  // The product the visitor is currently on (from the /products/<handle> path).
  function getProductHandle() {
    try {
      var m = location.pathname.match(/\/products\/([^\/?#]+)/);
      return m ? decodeURIComponent(m[1]) : '';
    } catch (e) { return ''; }
  }

  // --- A/B helpers (mirror hero-ab-test.js conventions) ---------------------
  function lsGet(k) { try { return localStorage.getItem(k); } catch (e) { return null; } }
  function lsSet(k, v) { try { localStorage.setItem(k, v); } catch (e) {} }

  // FNV-1a, same as hero-ab-test.js so bucketing is consistent across experiments.
  function hashString(value) {
    var hash = 2166136261, s = String(value || '');
    for (var i = 0; i < s.length; i++) {
      hash ^= s.charCodeAt(i);
      hash += (hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24);
    }
    return Math.abs(hash >>> 0);
  }

  function getVisitorId() {
    // Reuse hero-ab-test's id if present so a visitor is identified consistently.
    var existing = lsGet('hero-ab-test:visitor-id') || lsGet(AB_VISITOR_KEY);
    if (existing) return existing;
    var src;
    if (window.crypto && typeof window.crypto.randomUUID === 'function') src = window.crypto.randomUUID();
    else src = [Date.now(), Math.random(), navigator.userAgent || '', location.host || ''].join('|');
    var id = 'anon_' + src;
    lsSet(AB_VISITOR_KEY, id);
    return id;
  }

  function resolveVariant(params) {
    var alloc = Math.max(0, Math.min(100, AB_ALLOCATION));
    // 0 / 100 are global switches and must override a stored bucket, otherwise
    // already-bucketed visitors would never roll back/forward.
    if (alloc <= 0) return 'control';   // experiment off -> everyone keeps the original style
    if (alloc >= 100) return 'test';    // full rollout  -> everyone sees the new display
    var ov = params && (params.get(AB_OVERRIDE_PARAM) || '').toLowerCase();
    if (ov === 'test' || ov === 'control') return ov;            // QA override
    var stored = lsGet(AB_ASSIGN_KEY);
    if (stored === 'test' || stored === 'control') return stored; // keep visitors in their bucket
    var variant = (hashString(AB_EXPERIMENT_KEY + '|' + getVisitorId()) % 100) < alloc ? 'test' : 'control';
    lsSet(AB_ASSIGN_KEY, variant);
    return variant;
  }

  function abSegmentAndExpose(variant) {
    // Segment every subsequent OpenPanel event by the variant.
    try {
      if (typeof window.op === 'function') {
        var g = {};
        g[AB_VARIANT_PROP] = variant;
        g.pdp_discount_experiment_key = AB_EXPERIMENT_KEY;
        window.op('setGlobalProperties', g);
      }
    } catch (e) {}
    // Fire the exposure once per session, deduped per product + discount so a
    // second discount link (different product/code) in the same session still counts.
    var exposureKey = AB_EXPOSED_KEY + ':' + variant + ':' + (targetHandle || '') + ':' + (discountCode || '');
    var exposed = false;
    try { exposed = sessionStorage.getItem(exposureKey) === '1'; } catch (e) {}
    if (exposed) return;
    if (window.OpenPanel && typeof window.OpenPanel.track === "function")
      OpenPanel.track("PDP Discount Price Viewed", {
        experiment_key: AB_EXPERIMENT_KEY,
        variant: variant,
        allocation: AB_ALLOCATION,
        product_handle: targetHandle || '',
        discount_code: discountCode || '',
        discount_type: discount ? discount.type : '',
        discount_value: discount ? discount.value : '',
        page_url: location.href
      });
    try { sessionStorage.setItem(exposureKey, '1'); } catch (e) {}
  }

  // Derive the money format (symbol/prefix/suffix, separators, decimals) from an
  // existing price string so we can reformat amounts in the exact same style.
  function parseMoneyTemplate(str) {
    if (!str) return null;
    var m = String(str).match(/\d[\d.,]*\d|\d/);
    if (!m) return null;
    var token = m[0];
    // Keep only the formatting that belongs to THIS price. On-sale blocks (<del><ins>)
    // or a currency app that injects a hidden duplicate price put a second number nearby;
    // trim it so the suffix doesn't swallow it (which produced "$89 USD $99 USD").
    var prefix = str.slice(0, m.index).replace(/[\s\S]*\d/, '');               // drop up to any preceding digit
    var suffix = str.slice(m.index + token.length).replace(/\d[\s\S]*$/, '');  // drop from the next digit on
    suffix = suffix.replace(/(\S[\s\S]*?)\s*[$€£¥₩₹]\s*$/, '$1');              // drop a dangling next-price symbol
    var decSep = '.', thouSep = ',', decimals = 2;
    var lastSep = Math.max(token.lastIndexOf('.'), token.lastIndexOf(','));
    if (lastSep === -1) {
      decimals = 0; thouSep = '';
    } else {
      var sepChar = token.charAt(lastSep);
      var tail = token.slice(lastSep + 1).replace(/[^\d]/g, '');
      if (tail.length === 2) {
        decSep = sepChar; decimals = 2; thouSep = sepChar === '.' ? ',' : '.';
      } else {
        decimals = 0; thouSep = sepChar; decSep = sepChar === '.' ? ',' : '.';
      }
    }
    return { prefix: prefix, suffix: suffix, decSep: decSep, thouSep: thouSep, decimals: decimals };
  }

  function formatWithTemplate(cents, tpl) {
    if (!tpl) return '';
    var fixed = (Number(cents) / 100).toFixed(tpl.decimals);
    var parts = fixed.split('.');
    var intPart = parts[0];
    if (tpl.thouSep) intPart = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, tpl.thouSep);
    var num = tpl.decimals > 0 ? intPart + tpl.decSep + (parts[1] || '') : intPart;
    return tpl.prefix + num + tpl.suffix;
  }

  var discount = null;       // resolved once on init
  var discountCode = '';     // ?discount= value, shown on the tag
  var targetHandle = null;   // the ONE product handle this discount belongs to
  var formatTemplate = null; // money format captured from the original price
  var badgeTarget = null;    // the one price block that also gets the cart-style tag
  var trackedPrice = null;   // current variant base price (cents), from variant:changed
  var writing = false;       // re-entrancy guard for the MutationObserver

  function baseCentsFor(el) {
    if (trackedPrice != null && isFinite(trackedPrice)) return trackedPrice;
    var n = parseInt(el.getAttribute('data-base-price'), 10);
    return isFinite(n) ? n : NaN;
  }

  // Inline discount tag, placed right after the price (inside .t4s-product-price).
  function badgeHtml(base, newCents) {
    var saved = base - newCents;
    if (!isFinite(saved) || saved <= 0) return '';
    var codeText = discountCode ? escapeHtml(discountCode) + ' ' : '';
    var label = codeText + '(-' + escapeHtml(formatWithTemplate(saved, formatTemplate)) + ')';
    return ' <span class="t4s-url-discount-tag" data-url-discount-tag>' +
             '<span class="t4s-order-discount__item">' + TAG_ICON + ' ' + label + '</span>' +
           '</span>';
  }

  function applyTo(el) {
    if (!discount || !el || !formatTemplate) return;
    // Only ever render on the product the discount belongs to (guards AJAX swaps too).
    if (targetHandle != null && getProductHandle() !== targetHandle) return;

    var base = baseCentsFor(el);
    if (!isFinite(base) || base <= 0) return;

    var newCents = discountedCents(base, discount);
    if (newCents >= base || newCents <= 0) return; // nothing worth showing / never render a $0 price

    if (el.getAttribute('data-url-discount-for') === String(base)) return; // already rendered

    var html = '<del data-url-discount-del>' + escapeHtml(formatWithTemplate(base, formatTemplate)) + '</del> ' +
               '<ins data-url-discount-ins>' + escapeHtml(formatWithTemplate(newCents, formatTemplate)) + '</ins>';
    if (el === badgeTarget) {
      html += badgeHtml(base, newCents);
      el.classList.add('t4s-has-url-discount'); // flex-center the price + tag (see CSS)
    }

    writing = true;
    el.innerHTML = html;
    el.setAttribute('data-url-discount-for', String(base));
    setTimeout(function () { writing = false; }, 0);
  }

  function priceBlocks() {
    return document.querySelectorAll(PRICE_SELECTOR);
  }

  function applyAll() {
    var nodes = priceBlocks();
    for (var i = 0; i < nodes.length; i++) applyTo(nodes[i]);
  }

  // Render the discounted price + tag (variant 'test' only). DOM-dependent.
  function startRendering() {
    var first = document.querySelector(PRICE_SELECTOR);
    if (!first) return;
    badgeTarget = first;
    // Capture the money format from the pristine, server-rendered price (before we touch it).
    // Prefer the active selling price (<ins>) so we read exactly one money value even when
    // the product is already on a catalog sale (<del> compare + <ins> price).
    var fmtSrc = first.querySelector('ins') || first;
    formatTemplate = parseMoneyTemplate((fmtSrc.textContent || '').trim());
    if (!formatTemplate) return; // unknown format -> do not risk a wrong-looking price

    applyAll();

    // Re-apply after the theme re-renders the price on variant change.
    if (window.jQuery) {
      window.jQuery(document).on('variant:changed', function (evt) {
        var v = evt && evt.currentVariant;
        if (v && typeof v.price === 'number') trackedPrice = v.price;
        var nodes = priceBlocks();
        for (var i = 0; i < nodes.length; i++) nodes[i].removeAttribute('data-url-discount-for');
        requestAnimationFrame(applyAll);
      });
    }

    // Safety net: re-apply whenever the theme re-renders the price block through a
    // path we did not hook. Guarded against our own writes to avoid an infinite loop.
    if ('MutationObserver' in window) {
      var obs = new MutationObserver(function (mutations) {
        if (writing) return;
        for (var i = 0; i < mutations.length; i++) {
          var t = mutations[i].target;
          var el = t && t.closest ? t.closest(PRICE_SELECTOR) : null;
          if (el && !el.querySelector('[data-url-discount-ins]')) {
            el.removeAttribute('data-url-discount-for');
            applyTo(el);
          }
        }
      });
      var nodes = priceBlocks();
      for (var j = 0; j < nodes.length; j++) obs.observe(nodes[j], { childList: true, subtree: true });
    }
  }

  // Resolve discount + A/B variant + tracking ASAP (no DOM needed), then render
  // only for the 'test' bucket. The 'control' bucket keeps the original style.
  function bootstrap() {
    var params = getParams();
    var handle = getProductHandle();
    var fromUrl = resolveDiscount(params);
    if (fromUrl) {
      // A discount in the URL applies to THE product being viewed; bind it to that handle.
      discount = fromUrl;
      discountCode = fromUrl.code || '';
      targetHandle = handle;
      saveDiscount(discount, discountCode, handle);
    } else {
      // Only restore a persisted discount on the SAME product it was applied to,
      // so it survives refresh / returning to that product but does not bleed onto others.
      var stored = loadDiscount();
      if (stored && stored.handle && handle && stored.handle === handle) {
        discount = stored.discount;
        discountCode = stored.code;
        targetHandle = handle;
      }
    }
    if (!discount) return; // not the discounted product -> no experiment, page unchanged

    var variant = resolveVariant(params);
    abSegmentAndExpose(variant);     // segment all OpenPanel events + fire exposure
    if (variant !== 'test') return;  // control -> original price style (discount still applies at checkout)

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', safe(startRendering));
    } else {
      safe(startRendering)();
    }
  }

  // A price decoration must never break the product page; swallow any unexpected error.
  function safe(fn) {
    return function () { try { return fn.apply(this, arguments); } catch (e) {} };
  }

  safe(bootstrap)();
})();
